import { useEffect, useState } from 'react'
import { OWNER_METADATA_LABEL, type GovernanceDetail, type GovernanceInput, type OwnerMetadataField } from '@/api/clients/assessmentsApi'
import { Chip } from '@/components/readiness/primitives'
import { formatDate } from '@/components/common/format'

// GV-1's two parts (CSA governance and accountability). Part i is read from
// Discovery: the owner and classification fields on the systems in scope.
// Part ii is entered here by an Assessor. One part supports L1, both L2; the
// suggestion follows the next refresh and the Assessor still decides the level.

const FIELDS: Array<{ key: keyof GovernanceInput; label: string; placeholder: string; optional?: boolean }> = [
  { key: 'programme_owner', label: 'Quantum-safe programme owner', placeholder: 'Name and role, e.g. Jane Tan, CISO' },
  { key: 'decision_authority', label: 'Decision authority', placeholder: 'Who decides, e.g. CIO through the Technology Risk Committee' },
  { key: 'oversight_body', label: 'Oversight body', placeholder: 'e.g. Board Risk Committee, quarterly', optional: true },
  { key: 'raci_reference', label: 'RACI or governance charter', placeholder: 'Document title or link' },
]

const EMPTY: GovernanceInput = { programme_owner: '', decision_authority: '', oversight_body: '', raci_reference: '' }

export interface GovernancePanelProps {
  governance: GovernanceDetail
  // Absent when the assessment is read-only or the person cannot assess.
  onSave?: (input: GovernanceInput) => void
  // Why the form is read-only, when it is because of the person's role.
  readOnlyNote?: string
  busy?: boolean
  error?: string | null
}

const Met = ({ met, testId }: { met: boolean; testId: string }) => (
  <span data-testid={testId} data-met={met}><Chip tone={met ? 'ok' : 'neutral'}>{met ? 'Met' : 'Not met'}</Chip></span>
)

export function GovernancePanel({ governance, onSave, busy, error, readOnlyNote }: GovernancePanelProps) {
  const [form, setForm] = useState<GovernanceInput>(governance.input ?? EMPTY)
  useEffect(() => setForm(governance.input ?? EMPTY), [governance.input])
  const m = governance.metadata
  const dirty = FIELDS.some(({ key }) => form[key].trim() !== (governance.input?.[key] ?? '').trim())
  const missing = m ? (Object.entries(m.missing_by_field) as Array<[OwnerMetadataField, number]>).filter(([, n]) => n > 0) : []

  return (
    <section className="space-y-3" data-testid="quickview-governance">
      <p className="rd-label">How GV-1 is assessed</p>
      <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--rd-text-secondary)' }}>
        Two parts. One part met supports L1; both support L2. L3 is set by an assessor.
      </p>

      <div className="rounded-[10px] p-3" style={{ background: 'var(--rd-bg-card)', border: '1px solid var(--rd-border-subtle)' }}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12.5px] font-semibold">Part i · System owners in Discovery</p>
          <Met met={governance.part_one_met} testId="governance-part-one" />
        </div>
        {!m ? (
          <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Discovery is not connected, so this part cannot be checked yet.</p>
        ) : (
          <>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--rd-text-secondary)' }}>
              {m.complete_count} of {m.system_count} {m.scope === 'prioritised' ? 'prioritised systems' : 'systems'} have all six fields: business owner, technical owner, business unit, department, criticality and regulatory scope.
            </p>
            {missing.length > 0 && (
              <p className="mt-1 text-[12px]" style={{ color: 'var(--rd-text-muted)' }}>
                Missing: {missing.map(([f, n]) => `${OWNER_METADATA_LABEL[f]} (${n})`).join(', ')}. Fill these in Discovery; they count after the next sync.
              </p>
            )}
          </>
        )}
      </div>

      <div className="rounded-[10px] p-3" style={{ background: 'var(--rd-bg-card)', border: '1px solid var(--rd-border-subtle)' }}>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12.5px] font-semibold">Part ii · Programme governance</p>
          <Met met={governance.part_two_met} testId="governance-part-two" />
        </div>
        <p className="mt-1 text-[12px]" style={{ color: 'var(--rd-text-muted)' }}>
          Met when the programme owner, decision authority and RACI or charter are filled in.
          {governance.updated_by && governance.updated_at ? ` Last updated by ${governance.updated_by.display_name} · ${formatDate(governance.updated_at)}.` : ''}
        </p>
        <div className="mt-2 space-y-2">
          {FIELDS.map(({ key, label, placeholder, optional }) => (
            <label key={key} className="block text-[12px]">
              <span style={{ color: 'var(--rd-text-secondary)' }}>{label}{optional ? ' (optional)' : ''}</span>
              {onSave ? (
                <input
                  className="rd-input mt-1 !text-[12.5px]"
                  value={form[key]}
                  maxLength={500}
                  placeholder={placeholder}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  data-testid={`governance-${key}`}
                />
              ) : (
                <span className="mt-0.5 block text-[12.5px]">{governance.input?.[key] || '—'}</span>
              )}
            </label>
          ))}
        </div>
        {!onSave && readOnlyNote && (
          <p className="mt-2 text-[12px]" style={{ color: 'var(--rd-text-muted)' }} data-testid="governance-read-only">{readOnlyNote}</p>
        )}
        {onSave && (
          <button
            type="button"
            className="rd-btn rd-btn-sm rd-btn-primary mt-3"
            disabled={!dirty || busy}
            onClick={() => onSave(form)}
            data-testid="governance-save"
          >
            {busy ? 'Saving…' : 'Save governance input'}
          </button>
        )}
        {error && <p className="mt-2 text-[12px]" style={{ color: 'var(--rd-status-danger, var(--rd-status-warn))' }} role="alert">{error}</p>}
      </div>
    </section>
  )
}
